import {
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { memo, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { NumericFormat } from 'react-number-format';
import { useFormik } from 'formik';
import * as yup from 'yup';

import useTopPanel from 'hooks/useTopPanel';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useFormSubmit from 'hooks/useFormSubmit';
import FormActionButtons from 'components/ui/form/FormActionButtons';
import FormSelectField from 'components/ui/form/FormSelectField';
import FormDateField from 'components/ui/form/FormDateField';
import useMenu from 'hooks/useMenu';

const validationSchema = yup.object({
  deliver_id: yup.number().required("Mas'ul shaxs tanlanishi majburiy"),
  until_date: yup.string().required('Yetkazib berish sanasi majbiriy!'),
});

const AddOrder = () => {
  const { setComponent } = useTopPanel();
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const [hasError, setHasError] = useState(false);
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState('');
  const { submit, isSubmitting } = useFormSubmit();
  const { id } = useParams();
  const { hasChild } = useMenu();

  const view = (child) => {
    return hasChild('ORDER', child);
  };

  useEffect(() => {
    setComponent(
      <div className='component-title'>
        {id ? 'Buyurtmani tahrirlash' : "Buyurtma qo'shish"}
      </div>
    );
  }, []);

  const formik = useFormik({
    initialValues: {
      until_date: '',
      deliver_id: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      const newValues = {
        ...values,
        status: 1,
        products: products
          .filter((pr) => pr?.amount)
          .map(({ product_id, amount }) => ({
            product_id,
            amount,
          })),
      };
      const url = '/admin/orders';
      const message = id ? 'Buyurtma tahrirlandi' : "Yangi buyurtma qo'shildi";
      const type = id ? 'postUpdate' : 'post';

      submit(
        { type, contentType: 'simple' },
        newValues,
        url,
        message,
        id,
        false,
        handleClose
      );
    },
  });

  const handleClose = () => {
    navigate('/admin/order');
    formik.resetForm();
  };

  const { isLoading } = useQuery({
    queryKey: '/dictionary/products',
    queryFn: async () => {
      const res = await axiosPrivate.get('/dictionary/products');
      return res.data.data?.map((pr) => ({
        ...pr,
        product_id: pr?.id,
        unit: pr?.unit?.name,
        category: pr?.category?.name,
      }));
    },
    enabled: !hasError && !id,
    onSuccess: (data) => setProducts(data),
    onError: () => setHasError(true),
    retry: false,
  });

  const { data: selected, isLoading: isLoadingById } = useQuery({
    queryKey: ['/admin/orders/edit', id],
    queryFn: async () => {
      const res = await axiosPrivate.get(`/admin/orders/edit/${id}`);
      return res.data.data;
    },
    enabled: !hasError && !!id,
    onSuccess: (data) => {
      formik.setValues({
        deliver_id: data?.deliver_id,
        until_date: data?.until_date || '',
      });
      setProducts(
        data?.products?.map((pr) => ({
          ...pr,
          product_id: pr?.id,
          unit: pr?.unit?.name,
          category: pr?.category?.name,
        }))
      );
    },
    onError: () => setHasError(true),
    retry: false,
  });

  const setValue = (index, field, value) => {
    setProducts((prev) =>
      prev.map((pr, i) => (i === index ? { ...pr, [field]: value } : pr))
    );
  };

  const filteredData = useMemo(() => {
    if (!products.length) return [];

    let result = products;

    if (formik.values.category_id) {
      result = result.filter((p) => p.category_id == formik.values.category_id);
    }

    if (filter) {
      result = result.filter((p) =>
        p?.name?.toUpperCase()?.includes(filter?.toUpperCase())
      );
    }

    return result;
  }, [products, formik.values.category_id, filter]);

  return (
    <div className='component-list-wrapper'>
      <div className='component-table-wrapper h-[calc(100vh-80px)]'>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <FormSelectField
                label='Yetkazib beruvchi'
                fieldName='deliver_id'
                formik={formik}
                path='/dictionary/staffs'
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormDateField
                delay={0}
                label={'Yetkazib berish sanasi'}
                fieldName='until_date'
                formik={formik}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <div className='flex items-center gap-4'>
                <div className='[&>div]:flex [&>div]:whitespace-nowrap [&>div]:mt-2'>
                  <FormActionButtons
                    isSubmitting={isSubmitting}
                    disabled={!products.some((pr) => pr.amount > 0)}
                    formType='dialog'
                    setOpen={handleClose}
                    reset={formik.resetForm}
                  />
                </div>
              </div>
            </Grid>
          </Grid>
        </form>

        <TableContainer className='tab-table-container !shadow-none !base-border h-[calc(100vh-140px)]'>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell size='small' className='!bg-gray-50'>
                  #
                </TableCell>
                <TableCell size='small' className='!bg-gray-50 !w-[450px]'>
                  <div className='flex items-center gap-2'>
                    <span>Nomi</span>
                    <TextField
                      size='small'
                      fullWidth
                      placeholder='Qidiruv'
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position='end'>
                            <IconButton onClick={() => setFilter('')}>
                              <i className='bi bi-x' />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                </TableCell>
                <TableCell size='small' className='!bg-gray-50'>
                  <div className='flex items-center gap-2'>
                    <span>Kategoriyasi</span>
                    <FormSelectField
                      delay={0.4}
                      label={'Kategoriya'}
                      fieldName='category_id'
                      formik={formik}
                      autocomplete='off'
                      path={'/dictionary/product-category'}
                      endAdornment={
                        <InputAdornment position='end'>
                          <IconButton
                            onClick={() =>
                              formik.setFieldValue('category_id', '')
                            }
                          >
                            <i className='bi bi-x' />
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                  </div>
                </TableCell>
                <TableCell size='small' className='!bg-gray-50'>
                  Birlik qiymat
                </TableCell>
                <TableCell size='small' className='!bg-gray-50'>
                  Miqdori
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading || isLoadingById ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : filteredData.length ? (
                filteredData?.map((row, rowIndex) => (
                  <TableRow
                    className='bg-white hover:bg-slate-50'
                    key={rowIndex}
                  >
                    <TableCell size='small'>{rowIndex + 1}</TableCell>
                    <TableCell size='small'>{row?.name || ''}</TableCell>
                    <TableCell size='small'>{row?.category || ''}</TableCell>
                    <TableCell size='small'>{row?.unit || ''}</TableCell>
                    <TableCell size='small'>
                      <NumericFormat
                        value={Number(row.amount) || ''}
                        onChange={(e) => {
                          const val = parseFloat(
                            e.target.value.replace(/\s/g, '')
                          );
                          setValue(rowIndex, 'amount', val);
                        }}
                        customInput={TextField}
                        allowNegative={false}
                        thousandSeparator=' '
                        decimalScale={3}
                        size='small'
                        fullWidth
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align='center'>
                    Ma'lumot topilmadi
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  );
};

export default memo(AddOrder);
